// Shared chrome for the credits sheets: an in-place panel over the current
// page (centered card on desktop, bottom sheet on phones). The page underneath
// stays mounted, so a listing in progress is never lost.

import { useEffect, useLayoutEffect, useRef } from "react";
import styled from "styled-components";
import { registerSheetFocus } from "./sheetFocus";

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(11, 28, 30, 0.42);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;

  @media (max-width: 575.98px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const Panel = styled.div`
  background: #fff;
  border: 1px solid var(--brand-border, #e3ebec);
  border-radius: 18px;
  box-shadow: var(--brand-shadow-lg, 0 12px 32px rgba(13, 59, 57, 0.1));
  width: 100%;
  max-width: 480px;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  padding: 24px 26px 22px;

  @media (max-width: 575.98px) {
    border-radius: 18px 18px 0 0;
    max-height: 92vh;
    padding: 20px 18px 22px;
  }
`;

export const SheetTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--brand-ink, #0b1c1e);
  margin: 0 0 8px;
`;

export const SheetText = styled.p`
  font-size: 14px;
  line-height: 1.55;
  color: var(--brand-ink-3, #5c7074);
  margin: 0 0 12px;
`;

export const SheetActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  margin-top: 18px;
  flex-wrap: wrap;
`;

export const SheetPrimary = styled.button`
  background: var(--brand-primary, #0e7e7a);
  color: #fff;
  border: none;
  border-radius: var(--brand-radius, 10px);
  padding: 11px 22px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: var(--brand-shadow-primary);

  &:hover:not(:disabled) {
    background: var(--brand-primary-600, #0b6360);
  }

  &:disabled {
    background: var(--brand-border-strong, #c9d6d8);
    cursor: not-allowed;
    box-shadow: none;
  }
`;

export const SheetSecondary = styled.button`
  background: none;
  border: 1px solid var(--brand-border-strong, #c9d6d8);
  color: var(--brand-ink-2, #2b3f42);
  border-radius: var(--brand-radius, 10px);
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--brand-bg-soft, #f7fafb);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const SheetError = styled.p`
  color: #b91c1c;
  font-size: 13.5px;
  margin: 12px 0 0;
`;

/**
 * @param {{
 *   labelledBy: string,
 *   onClose?: () => void,
 *   returnFocusRef?: React.RefObject<HTMLElement>,
 *   children: React.ReactNode,
 * }} props
 */
const Sheet = ({ labelledBy, onClose, returnFocusRef, children }) => {
  const panelRef = useRef(/** @type {HTMLDivElement|null} */ (null));
  const focusRef = useRef(/** @type {ReturnType<typeof registerSheetFocus>|null} */ (null));
  const returnRef = useRef(returnFocusRef);
  returnRef.current = returnFocusRef;
  useLayoutEffect(() => {
    if (!panelRef.current) return undefined;
    const lifecycle = registerSheetFocus(
      panelRef.current,
      () => returnRef.current?.current || null
    );
    focusRef.current = lifecycle;
    return () => {
      focusRef.current = null;
      lifecycle.close();
    };
  }, []);
  useLayoutEffect(() => focusRef.current?.repair());

  useEffect(() => {
    if (!onClose) return undefined;
    /** @param {KeyboardEvent} e */
    const onKey = (e) => {
      if (e.key === "Escape" && !e.defaultPrevented && focusRef.current?.isTop()) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <Backdrop
      data-testid="credits-sheet-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget && focusRef.current?.isTop() && onClose) onClose();
      }}
    >
      <Panel
        ref={panelRef}
        // biome-ignore lint/a11y/useSemanticElements: preserve existing sheet chrome; native-dialog migration is outside this focus-only change
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        onFocusCapture={(e) => focusRef.current?.remember(e.target)}
      >
        {children}
      </Panel>
    </Backdrop>
  );
};

export default Sheet;
